import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import moment from 'moment'
import TopRail from '../HMI/DashboardV2/TopRail'
import ArAgingHeader from './ArAgingHeader'
import ArStatTiles from './ArStatTiles'
import ArFilterBar from './ArFilterBar'
import ArCompanyList from './ArCompanyList'
import ArDetailPanel from './ArDetailPanel'
import ArCollectionsDialog from './ArCollectionsDialog'
import ArStatementPreview from './ArStatementPreview'
import ArToast from './ArToast'
import { companyKeyOf } from './arAgingKeys'
import {
  pastDueOf, sumBuckets, filterCompanies, sortCompanies, cycleSort, csvRows, currencyFormat0
} from './arAgingModel'
import '../../styles/hmiReskin.css'
import '../HMI/DashboardV2/dashboardV2.css'
import './arAging.css'

const DEFAULT_FILTERS = {
  severity: 'all', bucket: '', pastDueOnly: false, search: '', minBalance: '',
  branchId: '', lastPayment: '', lastActivity: '', pausedOnly: false
}

const ArAgingDashboard = (props) => {
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS)
  const [sort, setSort] = React.useState({ key: 'attention', dir: 'desc' })

  const [selectedKey, setSelectedKey] = React.useState(null)
  const [openMenuKey, setOpenMenuKey] = React.useState(null)
  const [checkedKeys, setCheckedKeys] = React.useState(() => new Set())

  // PTP + run exclusions are client-only this pass (session state).
  // TODO(server): persist promise-to-pay and per-run exclusions.
  const [ptpByCompany, setPtpByCompany] = React.useState({})
  const [excludedKeys, setExcludedKeys] = React.useState(() => new Set())

  const [previewOpen, setPreviewOpen] = React.useState(false)
  const [previewIndex, setPreviewIndex] = React.useState(0)

  const [dialog, setDialog] = React.useState(null) // { type, companyKey }
  const [dialogBusy, setDialogBusy] = React.useState(false)
  const [dialogError, setDialogError] = React.useState('')
  const [toast, setToast] = React.useState(null)
  const [activityVersion, setActivityVersion] = React.useState(0)

  const endpoint = `${props.url}/api/aspire/ar-aging/collections`
  const showToast = React.useCallback((message) => setToast(message), [])

  const fetchData = async (refresh) => {
    setLoading(true)
    setError(null)
    try {
      const response = await axios.get(`${props.url}/api/aspire/ar-aging${refresh ? '?refresh=1' : ''}`)
      setData(response.data)
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'AR aging pull failed')
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { fetchData(false) }, [])

  const visibleCompanies = React.useMemo(() => {
    if (!data?.companies) return []
    return sortCompanies(filterCompanies(data.companies, filters), sort, ptpByCompany)
  }, [data, filters, sort, ptpByCompany])

  // Tiles + count line describe the FILTERED set so the numbers always match.
  const visibleTotals = React.useMemo(() => {
    const totals = sumBuckets(visibleCompanies.flatMap(company => company.properties))
    totals.pastDue = pastDueOf(totals)
    return totals
  }, [visibleCompanies])
  const visiblePastDue = visibleTotals.pastDue
  const visiblePropertyCount = visibleCompanies.reduce((count, company) => count + company.properties.length, 0)

  // Selection drives the detail panel; if the selection falls out of view, fall
  // back to the first visible company.
  const selectedCompany = React.useMemo(() => {
    if (!visibleCompanies.length) return null
    return visibleCompanies.find(company => companyKeyOf(company) === selectedKey) || visibleCompanies[0]
  }, [visibleCompanies, selectedKey])
  const effectiveSelectedKey = selectedCompany ? companyKeyOf(selectedCompany) : null

  // Patch a company in place (mirrors flag/lastActivity changes into the list).
  const patchCompany = (companyId, patch) => {
    if (companyId == null) return
    setData(prev => prev
      ? { ...prev, companies: prev.companies.map(company => company.companyId === companyId ? { ...company, ...patch } : company) }
      : prev)
  }

  const logActivity = async (company, payload) => {
    const topProperty = [...(company.properties || [])].sort((a, b) => pastDueOf(b.buckets) - pastDueOf(a.buckets))[0]
    await axios.post(`${endpoint}/activities`, {
      aspireCompanyId: company.companyId,
      aspirePropertyId: topProperty?.propertyId ?? null,
      ...payload
    })
    patchCompany(company.companyId, { lastActivityAt: new Date().toISOString() })
    setActivityVersion(version => version + 1)
  }

  // Comm buttons: fire the native link AND log the touch (log-first).
  const handleCall = (company) => {
    const contact = (company.billingContacts || []).find(person => person.phone)
    if (company.companyId != null) logActivity(company, { subtype: 'call', contactName: contact?.name, meta: { phone: contact?.phone } }).catch(() => {})
    if (contact?.phone) window.location.href = `tel:${contact.phone}`
    showToast('Call logged')
  }
  const handleEmail = (company) => {
    const contact = (company.billingContacts || []).find(person => person.email)
    if (company.companyId != null) logActivity(company, { subtype: 'email', contactName: contact?.name, meta: { email: contact?.email } }).catch(() => {})
    if (contact?.email) window.location.href = `mailto:${contact.email}`
    showToast('Email logged')
  }
  const handleSms = (company) => {
    const contact = (company.billingContacts || []).find(person => person.phone)
    if (!contact?.phone) { showToast('No contact phone on file'); return }
    const message = window.prompt(`Text ${contact.name || 'contact'} at ${contact.phone}:`, '')
    if (message == null || !message.trim()) return
    axios.post(`${endpoint}/sms`, { aspireCompanyId: company.companyId, to: contact.phone, message: message.trim(), contactName: contact.name })
      .then(() => { patchCompany(company.companyId, { lastActivityAt: new Date().toISOString() }); setActivityVersion(v => v + 1); showToast('SMS sent') })
      .catch(err => showToast(err?.response?.data?.message || 'SMS failed'))
  }
  const handleAddNote = (company) => {
    const note = window.prompt(`Add a collections note for ${company.companyName}:`, '')
    if (note == null || !note.trim()) return
    logActivity(company, { subtype: 'note', notes: note.trim() }).then(() => showToast('Note added')).catch(() => showToast('Note failed'))
  }
  const handleAddActivity = (company) => {
    const note = window.prompt(`Log a call for ${company.companyName}:`, '')
    if (note == null || !note.trim()) return
    logActivity(company, { subtype: 'call', notes: note.trim() }).then(() => showToast('Activity logged')).catch(() => showToast('Log failed'))
  }

  const toggleResume = (company) => {
    setDialogBusy(false)
    axios.post(`${endpoint}/pause-intent`, { aspireCompanyId: company.companyId, pauseRequested: false, notes: 'Pause request cleared' })
      .then(state => { patchCompany(company.companyId, { pauseRequested: state.data.pauseRequested, lastActivityAt: new Date().toISOString() }); setActivityVersion(v => v + 1); showToast('Pause request cleared') })
      .catch(err => showToast(err?.response?.data?.message || 'Resume failed'))
  }

  const openDialog = (type, company) => { setDialogError(''); setDialog({ type, companyKey: companyKeyOf(company) }) }

  // Shared action set used by both the row kebab and the detail-panel chips.
  const actionsFor = (company) => {
    const canAct = company.companyId != null
    const actions = [
      { label: 'CALL', run: handleCall },
      { label: 'SEND EMAIL', run: handleEmail },
      { label: 'SEND SMS', run: handleSms, disabled: !canAct },
      { label: 'ADD ACTIVITY', run: handleAddActivity, disabled: !canAct },
      { label: 'ADD NOTE', run: handleAddNote, disabled: !canAct },
      { label: 'LOG PROMISE TO PAY', run: (c) => openDialog('ptp', c), disabled: !canAct }
    ]
    if (!company.escalated) actions.push({ label: 'ESCALATE', tone: 'warn', run: (c) => openDialog('escalate', c), disabled: !canAct })
    if (company.pauseRequested) actions.push({ label: 'RESUME MTC SERVICES', run: toggleResume, disabled: !canAct })
    else actions.push({ label: 'PAUSE MTC SERVICES', tone: 'danger', run: (c) => openDialog('pause', c), disabled: !canAct })
    return actions
  }

  const dialogCompany = React.useMemo(() => {
    if (!dialog || !data?.companies) return null
    return data.companies.find(company => companyKeyOf(company) === dialog.companyKey) || null
  }, [dialog, data])

  const confirmDialog = async (payload) => {
    if (!dialogCompany) return
    setDialogBusy(true)
    setDialogError('')
    try {
      if (dialog.type === 'escalate') {
        const notes = [payload.reason, payload.note].filter(Boolean).join(' — ')
        const state = await axios.post(`${endpoint}/escalate`, { aspireCompanyId: dialogCompany.companyId, escalated: true, notes })
        patchCompany(dialogCompany.companyId, { escalated: state.data.escalated, lastActivityAt: new Date().toISOString() })
        showToast('Escalated — held from run')
      } else if (dialog.type === 'pause') {
        const state = await axios.post(`${endpoint}/pause-intent`, { aspireCompanyId: dialogCompany.companyId, pauseRequested: true, notes: payload.note || 'Maintenance pause requested' })
        patchCompany(dialogCompany.companyId, { pauseRequested: state.data.pauseRequested, lastActivityAt: new Date().toISOString() })
        showToast('Pause requested')
      } else if (dialog.type === 'ptp') {
        // Local-only: record the promise + log a collections activity for the trail.
        setPtpByCompany(prev => ({ ...prev, [dialogCompany.companyId]: { amount: payload.amount, inDays: payload.inDays } }))
        await logActivity(dialogCompany, { subtype: 'note', notes: `Promise to pay ${currencyFormat0(payload.amount)} in ${payload.inDays} days` }).catch(() => {})
        showToast('Promise logged')
      }
      setActivityVersion(version => version + 1)
      setDialog(null)
    } catch (requestError) {
      setDialogError(requestError?.response?.data?.message || requestError.message || 'Action failed')
    } finally {
      setDialogBusy(false)
    }
  }

  // Bulk selection helpers (keyed like selection).
  const toggleCheck = (company) => {
    const key = companyKeyOf(company)
    setCheckedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }
  const setExclusionForChecked = (excluded) => {
    setExcludedKeys(prev => {
      const next = new Set(prev)
      for (const key of checkedKeys) { if (excluded) next.add(key); else next.delete(key) }
      return next
    })
    showToast(excluded ? 'Excluded from run' : 'Included in run')
  }
  const bulkLog = (subtype, label) => {
    const targets = visibleCompanies.filter(company => checkedKeys.has(companyKeyOf(company)) && company.companyId != null)
    Promise.all(targets.map(company => logActivity(company, { subtype, notes: `${label} (bulk)` }).catch(() => {})))
      .then(() => showToast(`${label} · ${targets.length} companies`))
  }
  const bulkActions = [
    { label: 'Email all', run: () => bulkLog('email', 'Email all') },
    { label: 'SMS all', run: () => bulkLog('sms', 'SMS all') },
    { label: 'Exclude from run', run: () => setExclusionForChecked(true) },
    { label: 'Include in run', run: () => setExclusionForChecked(false) }
  ]

  const toggleExclude = (company) => {
    const key = companyKeyOf(company)
    setExcludedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  const onExport = () => {
    const csv = csvRows(visibleCompanies, ptpByCompany)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `ar-aging-${moment().format('YYYY-MM-DD')}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
    showToast(`Exported ${visibleCompanies.length} companies`)
  }

  return (
    <div className="ar-aging-root">
      <TopRail url={props.url} user={props.user} employees={props.employees} tasks={props.tasks} />
      <div className="ar-aging-content">
        <ArAgingHeader
          data={data}
          loading={loading}
          onRefresh={() => fetchData(true)}
          onExport={onExport}
          onPreview={() => { setPreviewIndex(0); setPreviewOpen(true) }}
        />

        {data?.stale && (
          <div className="ar-stale-banner">
            Aspire is unreachable — showing cached data from {moment(data.fetchedAt).format('M/D h:mm a')}.
          </div>
        )}

        {data && <ArStatTiles totals={visibleTotals} filters={filters} onFilterChange={setFilters} />}
        {data && <ArFilterBar filters={filters} onChange={setFilters} paymentFilterEnabled={data.source === 'db'} />}

        {data && (
          <div className="ar-count-line">
            SHOWING {visibleCompanies.length} OF {data.companies?.length || 0} COMPANIES · {visiblePropertyCount} PROPERTIES · {currencyFormat0(visiblePastDue)} PAST DUE
          </div>
        )}

        {loading && !data && <div className="ar-aging-status">Pulling AR aging from Aspire… first pull can take a minute.</div>}
        {error && !data && (
          <div className="ar-aging-status error">
            {error}
            <div style={{ marginTop: 12 }}>
              <button className="ar-btn ar-btn-outline" onClick={() => fetchData(true)}>Retry</button>
            </div>
          </div>
        )}

        {data && (
          <div className="ar-main-grid">
            <ArCompanyList
              companies={visibleCompanies}
              sort={sort}
              onSort={(key) => setSort(current => cycleSort(current, key))}
              selectedKey={effectiveSelectedKey}
              checkedKeys={checkedKeys}
              excludedKeys={excludedKeys}
              ptpByCompany={ptpByCompany}
              openMenuKey={openMenuKey}
              onSelect={(company) => setSelectedKey(companyKeyOf(company))}
              onToggleCheck={toggleCheck}
              onToggleMenu={(company) => setOpenMenuKey(company ? companyKeyOf(company) : null)}
              actionsFor={actionsFor}
              bulkActions={bulkActions}
              onClearChecked={() => setCheckedKeys(new Set())}
            />
            <ArDetailPanel
              company={selectedCompany}
              url={props.url}
              ptp={selectedCompany ? ptpByCompany[selectedCompany.companyId] : null}
              excluded={selectedCompany ? excludedKeys.has(effectiveSelectedKey) : false}
              actions={selectedCompany ? actionsFor(selectedCompany) : []}
              onLogNote={handleAddNote}
              activityVersion={activityVersion}
            />
          </div>
        )}
      </div>

      {dialog && dialogCompany && (
        <ArCollectionsDialog
          type={dialog.type}
          company={dialogCompany}
          busy={dialogBusy}
          error={dialogError}
          onCancel={() => { setDialog(null); setDialogError('') }}
          onConfirm={confirmDialog}
        />
      )}

      {previewOpen && (
        <ArStatementPreview
          companies={visibleCompanies}
          excludedKeys={excludedKeys}
          index={previewIndex}
          onIndexChange={setPreviewIndex}
          onToggleExclude={toggleExclude}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      <ArToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  employees: state.employees,
  tasks: state.tasks
})

export default connect(mapStateToProps)(ArAgingDashboard)
